import { Component, HostBinding, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AbbSimpleCardComponent } from '@components/abb-card/abb-simple-card.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { NotificationsService } from '@services/ui/notifications.service';
import { Apollo } from 'apollo-angular';
import { Event_Category, Notification_Type } from 'gql/graphql';
import { Subscription } from 'rxjs';

@Component({
  selector: 'abb-notifications',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AbbSimpleCardComponent,
    MatCheckboxModule,
    TPipe,
  ],
  template: `
    <abb-simple-card
      [title]="'Notifications' | t"
      [subtitle]="'Specify how you would like to receive notifications' | t"
    >
      <form [formGroup]="form" content>
        <table class="w-full border-separate border-spacing-5">
          <thead>
            <tr>
              <th class="w-1/5"></th>
              <th class="w-1/4 text-xs text-abb-font/80 font-bold uppercase">
                {{ 'Email' | t }}
              </th>
              <th class="w-1/4 text-xs text-abb-font/80 font-bold uppercase">
                {{ 'In-app / Push Notifications' | t }}
              </th>
              <th
                class="w-1/4 text-xs text-abb-font/80 font-bold uppercase hidden"
              >
                {{ 'SMS' | t }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr formGroupName="CRITICAL">
              <th
                class="w-1/5 text-left text-xs text-abb-font/80 font-bold uppercase"
              >
                {{ 'Critical events' | t }}
                <div
                  class="text-[11px] text-abb-font/60 font-normal normal-case mt-1"
                >
                  {{
                    'Notifications linked to alarms, automations and critical connection issues.'
                      | t
                  }}
                </div>
              </th>
              <td class="text-center">
                <mat-checkbox
                  formControlName="EMAIL"
                  name="criticalEventsEmail"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Critical,
                      notificationType: Notification_Type.Email
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center">
                <mat-checkbox
                  formControlName="INAPP_PUSH"
                  name="criticalEventsInAppPush"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Critical,
                      notificationType: Notification_Type.InappPush
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center hidden">
                <mat-checkbox
                  formControlName="SMS"
                  name="criticalEventsSms"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Critical,
                      notificationType: Notification_Type.Sms
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
            </tr>
            <tr formGroupName="INFORMATION">
              <th
                class="w-1/5 text-left text-xs text-abb-font/80 font-bold uppercase"
              >
                {{ 'Information Events' | t }}
                <div
                  class="text-[11px] text-abb-font/60 font-normal normal-case mt-1"
                >
                  {{
                    'General informative messages linked to non-critical connection issues.'
                      | t
                  }}
                </div>
              </th>
              <td class="text-center">
                <mat-checkbox
                  formControlName="EMAIL"
                  name="informationEventsEmail"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Information,
                      notificationType: Notification_Type.Email
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center">
                <mat-checkbox
                  formControlName="INAPP_PUSH"
                  name="informationEventsInAppPush"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Information,
                      notificationType: Notification_Type.InappPush
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center hidden">
                <mat-checkbox
                  formControlName="SMS"
                  name="informationEventsSms"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.Information,
                      notificationType: Notification_Type.Sms
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
            </tr>
            <tr formGroupName="SYSTEM">
              <th
                class="w-1/5 text-left text-xs text-abb-font/80 font-bold uppercase"
              >
                {{ 'System Events' | t }}
                <div
                  class="text-[11px] text-abb-font/60 font-normal normal-case mt-1"
                >
                  {{
                    'General information regarding the application (e.g. new upgrades, new functionalities, etc).'
                      | t
                  }}
                </div>
              </th>
              <td class="text-center">
                <mat-checkbox
                  formControlName="EMAIL"
                  name="systemEventsEmail"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.System,
                      notificationType: Notification_Type.Email
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center">
                <mat-checkbox
                  formControlName="INAPP_PUSH"
                  name="systemEventsInAppPush"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.System,
                      notificationType: Notification_Type.InappPush
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center hidden">
                <mat-checkbox
                  formControlName="SMS"
                  name="systemEventsSms"
                  (change)="
                    checkBoxChange({
                      eventType: Event_Category.System,
                      notificationType: Notification_Type.Sms
                    })
                  "
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    </abb-simple-card>
  `,
  styles: `
    .border-font-checkbox ::ng-deep .mdc-checkbox .mdc-checkbox__native-control:enabled:checked~.mdc-checkbox__background, .mdc-checkbox .mdc-checkbox__native-control:enabled:indeterminate~.mdc-checkbox__background, .mdc-checkbox .mdc-checkbox__native-control[data-indeterminate=true]:enabled~.mdc-checkbox__background {
        border-color: var(--abb-color-font) !important;
    }
  `,
  host: {
    class: 'p-2 pr-6',
  },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'settings.tabs.notifications',
    },
  ],
})
export class NotificationsComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'notifications-component';
  readonly Event_Category = Event_Category;
  readonly Notification_Type = Notification_Type;
  form = new FormGroup({
    CRITICAL: new FormGroup({
      EMAIL: new FormControl(false),
      INAPP_PUSH: new FormControl(false),
      SMS: new FormControl(false),
    }),
    INFORMATION: new FormGroup({
      EMAIL: new FormControl(false),
      INAPP_PUSH: new FormControl(false),
      SMS: new FormControl(false),
    }),
    SYSTEM: new FormGroup({
      EMAIL: new FormControl(false),
      INAPP_PUSH: new FormControl(false),
      SMS: new FormControl(false),
    }),
  });

  private subscriptions: Subscription[] = [];
  constructor(
    public apollo: Apollo,
    private notificationsService: NotificationsService
  ) {
    this.subscriptions.push(
      this.notificationsService.getNotificationsSettings().subscribe((res) => {
        if (res) {
          this.form.setValue({
            CRITICAL: {
              EMAIL: res!.CRITICAL.EMAIL,
              INAPP_PUSH: res!.CRITICAL.INAPP_PUSH,
              SMS: res!.CRITICAL.SMS,
            },
            INFORMATION: {
              EMAIL: res!.INFORMATION.EMAIL,
              INAPP_PUSH: res!.INFORMATION.INAPP_PUSH,
              SMS: res!.INFORMATION.SMS,
            },
            SYSTEM: {
              EMAIL: res!.SYSTEM.EMAIL,
              INAPP_PUSH: res!.SYSTEM.INAPP_PUSH,
              SMS: res!.SYSTEM.SMS,
            },
          });
        }
      })
    );
  }

  checkBoxChange(c: {
    eventType: Event_Category;
    notificationType: Notification_Type;
  }) {
    const { eventType, notificationType } = c;
    const notificationOn = this.form.value[eventType]![notificationType];
    if (notificationType === Notification_Type.InappPush) {
      this.notificationsService.toggleInAppPush(eventType, !!notificationOn);
    }
    this.notificationsService.updateNotificationsSettings(
      eventType,
      notificationType,
      !!notificationOn
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
